import {ActivatedRouteSnapshot, Router, RouterStateSnapshot, CanActivateFn} from '@angular/router'
import {inject} from '@angular/core'
import {Store} from '@ngrx/store'
import {selectUser} from '../app/store/auth.selector'
import {IUser} from '../app/store/auth.state'
import {MenuService} from '../all-services/new-services-after-change-uaa/menu.service'
import {map, switchMap, catchError, of, filter, take} from 'rxjs'
import {setMenu} from '../app/store/menu/menu.action'
import {selectMenu} from '../app/store/menu/menu.selector'
import {ToastrService} from 'ngx-toastr'

function hasMenuAccess(menu: any[], currentUrl: string): boolean {
  return menu.some(m => {
    if (!m.url) return false

    // Exact match for menu URLs
    if (currentUrl === m.url) return true

    // For nested routes, check if current URL starts with menu URL
    // but ensure it's a proper path match (not partial string match)
    if (currentUrl.startsWith(m.url)) {
      // Check if the next character after menu URL is either '/' or '?' or end of string
      const remainingPath = currentUrl.substring(m.url.length)
      return remainingPath === '' || remainingPath.startsWith('/') || remainingPath.startsWith('?')
    }

    return false
  })
}


export const isAuthenticatedGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const store = inject(Store)
  const router = inject(Router)
  const menuService = inject(MenuService)
  const toastr = inject(ToastrService)

  return store.select(selectUser).pipe(
    filter(user => user !== undefined), // Wait for user to be loaded (could be null if not authenticated)
    take(1),
    switchMap(user => {
      if (!user) {
        // Redirect to login page for unauthenticated users
        // console.log('User not authenticated, redirecting to login')
        void router.navigate(['/'])
        return of(false)
      }

      return validateMenuAccess(store, menuService, state, router, user, toastr)
    })
  )
}

function validateMenuAccess(
  store: Store,
  menuService: MenuService,
  state: RouterStateSnapshot,
  router: Router,
  user: IUser,
  toastr: ToastrService
) {
  return store.select(selectMenu).pipe(
    switchMap(menu => {
      if (menu) {
        // User has menu loaded, validate access
        const hasAccessToMenu = hasMenuAccess(menu, state.url)
        const isMainPage = state.url === '/lms-plus/panel'

        // console.log('Validating menu access:', {
        //   currentUrl: state.url,
        //   hasAccessToMenu,
        //   isMainPage,
        //   menuUrls: menu.map(m => m.url).filter(Boolean)
        // })

        // Always allow access to main panel page
        if (isMainPage) {
          // console.log('Allowing access to main panel page')
          return of(true)
        }

        // For other pages, check if user has access to the menu
        if (hasAccessToMenu) {
          // console.log('User has menu access, allowing entry')
          return of(true)
        }

         // User doesn't have access to specific page, show error and redirect to main panel
         // console.log('No menu access for specific page, redirecting to main panel')
         toastr.error('شما دسترسی به این صفحه را ندارید', 'دسترسی غیرمجاز')
         void router.navigate(['/lms-plus/panel'])
         return of(false)
      }

      // Menu not loaded yet, fetch it
      return menuService.getMyMenu().pipe(
        map(res => {
          const hasAccessToMenu = hasMenuAccess(res, state.url)
          const isMainPage = state.url === '/lms-plus/panel'

          // console.log('Fetched menu, validating access:', {
          //   currentUrl: state.url,
          //   hasAccessToMenu,
          //   isMainPage,
          //   menuUrls: res.map(m => m.url).filter(Boolean)
          // })

          // Always allow access to main panel and store menu
          if (isMainPage) {
            store.dispatch(setMenu({menu: res}))
            return true
          }

          // For other pages, check menu access
          if (hasAccessToMenu) {
            store.dispatch(setMenu({menu: res}))
            return true
          }

          // User doesn't have access, show error and redirect to main panel
          // console.log('No menu access after fetch, redirecting to main panel')
          toastr.error('شما دسترسی به این صفحه را ندارید', 'دسترسی غیرمجاز')
          void router.navigate(['/lms-plus/panel'])
          return false
        }),
        catchError(error => {
          // console.error('Error fetching menu', error)
          void router.navigate(['/'])
          return of(false)
        })
      )
    })
  )
}

